import * as React from 'react'
import {
  Create,
  CreateProps,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  DateInput,
  required,
  minValue,
  FormDataConsumer,
  useRedirect,
  Record,
} from 'react-admin'
import { Typography } from '@material-ui/core'
import { mainTheme } from '../../../utils/mainTheme'
import { useErrorHandler } from '../../../hooks/useCouponErrorHandler'
import { Coupon } from '@vacinas-net/shared'
import useStyles from '../../../utils/creationFormStyles'
import HeaderWithGoBack from '../../../components/HeaderWithGoBack'

const CouponCreate = (props: CreateProps) => {
  const classes = useStyles()

  const redirect = useRedirect()
  const { handleError, handleSuccess } = useErrorHandler()

  // Função de transformação dos dados
  const transform = (data: Record) => {
    const transformedData = {
      ...data,
      // Normalizar código para UPPERCASE
      code: data.code?.trim().toUpperCase(),
      // Converter datas para ISO string
      validFrom: data.validFrom
        ? new Date(data.validFrom).toISOString()
        : undefined,
      validUntil: data.validUntil
        ? new Date(data.validUntil).toISOString()
        : undefined,
      // Tratar eligibleItems - quando appliesTo é all_products, deve ser array vazio
      eligibleItems:
        data.appliesTo === 'all_products'
          ? []
          : data.eligibleItems.split(',') || [],
      // Tratar externalData - só incluir se tiver dados válidos
      externalData:
        data.externalData?.doktorLink?.medicoId ||
        data.externalData?.doktorLink?.campanhaId
          ? data.externalData
          : undefined,
    }

    // Log para debug
    console.log('Dados originais:', data)
    console.log('Dados transformados:', transformedData)
    return transformedData
  }

  // Validações customizadas
  const validateCode = (value: string) => {
    if (!value) return 'Código é obrigatório'
    if (value.length < 3) return 'Código deve ter pelo menos 3 caracteres'
    if (value.length > 64) return 'Código deve ter no máximo 64 caracteres'
    if (!/^[A-Z0-9_-]+$/.test(value))
      return 'Código deve conter apenas A-Z, 0-9, _ e -'
    return undefined
  }

  const validateDates = (allValues: Coupon) => {
    if (allValues.validFrom && allValues.validUntil) {
      const from = new Date(allValues.validFrom)
      const until = new Date(allValues.validUntil)
      if (until <= from)
        return 'Data de fim deve ser posterior à data de início'
    }
    return undefined
  }

  const validateValue = (value: number, allValues: Coupon) => {
    if (allValues.type === 'percentage' && (value < 1 || value > 100)) {
      return 'Porcentagem deve estar entre 1 e 100'
    }
    if (allValues.type === 'fixed' && value < 0) {
      return 'Valor fixo deve ser maior ou igual a 0'
    }
    if (allValues.type === 'fixedPrice' && value < 0) {
      return 'Preço fixo deve ser maior ou igual a 0'
    }
    return undefined
  }

  const validateEligibleItems = (value: string[], allValues: Coupon) => {
    if (allValues.appliesTo === 'all_products' && value && value.length > 0) {
      return 'Itens elegíveis devem estar vazios quando aplica-se a todos os produtos'
    }
    if (
      allValues.appliesTo !== 'all_products' &&
      (!value || value.length === 0)
    ) {
      return 'Itens elegíveis são obrigatórios quando não aplica-se a todos os produtos'
    }
    return undefined
  }

  return (
    <Create
      {...props}
      title="Criar Cupom"
      transform={transform}
      onSuccess={() => {
        handleSuccess('Cupom criado com sucesso!')
        redirect('/coupon')
      }}
      onFailure={(error) => {
        handleError(
          error,
          'Erro ao criar cupom. Verifique os dados e tente novamente.',
          'CouponCreate'
        )
      }}
    >
      <SimpleForm className={classes.tabContainer}>
        <HeaderWithGoBack title="Criar cupom" />
        <TextInput
          source="code"
          label="Código do Cupom"
          validate={[required(), validateCode]}
          helperText="3-64 caracteres, apenas A-Z, 0-9, _ e -"
          variant="outlined"
          fullWidth
        />

        <SelectInput
          source="status"
          label="Status"
          choices={[
            { id: 'active', name: 'Ativo' },
            { id: 'inactive', name: 'Inativo' },
            { id: 'expired', name: 'Expirado' },
            { id: 'archived', name: 'Arquivado' },
          ]}
          validate={required()}
          variant="outlined"
          fullWidth
        />

        <SelectInput
          source="type"
          label="Tipo de Desconto"
          choices={[
            { id: 'percentage', name: 'Porcentagem' },
            { id: 'fixed', name: 'Valor Fixo' },
            { id: 'fixedPrice', name: 'Preço Fixo' },
          ]}
          validate={required()}
          variant="outlined"
          fullWidth
        />

        <FormDataConsumer>
          {({ formData }) => (
            <NumberInput
              source="value"
              label="Valor"
              validate={[required(), validateValue]}
              helperText={
                formData?.type === 'percentage'
                  ? 'Valor em porcentagem (1-100)'
                  : formData?.type === 'fixed'
                  ? 'Valor fixo em centavos (ex: 500 para R$5,00)'
                  : 'Preço final em centavos (ex: 8500 para R$85,00)'
              }
              variant="outlined"
              fullWidth
            />
          )}
        </FormDataConsumer>

        <SelectInput
          source="appliesTo"
          label="Aplica-se a"
          choices={[
            { id: 'all_products', name: 'Todos os produtos' },
            { id: 'categories', name: 'Categorias específicas' },
            { id: 'specific_products', name: 'Produtos específicos' },
          ]}
          validate={required()}
          fullWidth
          variant="outlined"
        />

        <FormDataConsumer>
          {({ formData }) =>
            formData?.appliesTo !== 'all_products' && (
              <TextInput
                source="eligibleItems"
                label="Itens Elegíveis"
                validate={validateEligibleItems}
                helperText={
                  formData?.appliesTo === 'categories'
                    ? 'Digite as categorias separadas por vírgula (ex: vaccine, earring)'
                    : 'Digite os SKUs dos produtos separados por vírgula'
                }
                fullWidth
                defaultValue=""
                variant="outlined"
              />
            )
          }
        </FormDataConsumer>

        <DateInput
          source="validFrom"
          label="Válido de"
          validate={required()}
          fullWidth
          variant="outlined"
        />

        <DateInput
          source="validUntil"
          label="Válido até"
          validate={[required(), validateDates]}
          fullWidth
          variant="outlined"
        />

        <NumberInput
          source="usageLimit"
          label="Limite de Uso"
          validate={[required(), minValue(0)]}
          helperText="Digite 0 para uso ilimitado"
          fullWidth
          variant="outlined"
        />

        <FormDataConsumer>
          {() => (
            <div>
              <Typography
                variant="h2"
                style={{
                  color: mainTheme.palette.text.primary,
                  marginTop: '20px',
                }}
              >
                Dados Externos (Opcional)
              </Typography>
              <TextInput
                source="externalData.doktorLink.medicoId"
                label="ID do Médico (Doktor Link)"
                helperText="Identificador único do médico no Doktor Link"
                fullWidth
                variant="outlined"
              />
              <TextInput
                source="externalData.doktorLink.campanhaId"
                label="ID da Campanha (Doktor Link)"
                helperText="Identificador único da campanha no Doktor Link"
                fullWidth
                variant="outlined"
              />
            </div>
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  )
}

export default CouponCreate
